import React, { PropsWithChildren } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./ProfilePage.scss";
import HeaderWithTitleAndBackButton from "../../components/Headers/HeaderWithTitleAndBackButton/HeaderWithTitleAndBackButton";
import useAuth from "../../hooks/useAuth";
import DownloadAppPrompt from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../hooks/useDisclosure";
import ArrowRightIcon from "../../components/icons/ArrowRightIcon";
import ImageTikTok from "../../images/tiktok-rounded.png";
import { LINKS } from "../../utils/constants";
import FilledButton, {
  ButtonSizes,
} from "../../components/dashboard-page/FilledButton/FilledButton";
import SignOutModal from "../../components/modals/SignOutModal/SignOutModal";
import useMyTopGamesQuery from "../../react-query/queries/use-my-top-games";
import { ROUTES } from "../../utils/constants";
import { getTimeDifferenceToNow } from "../../utils/helpers";
import ImageIcon from "../../components/icons/ImageIcon";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import Skeleton from "../../components/Skeleton/Skeleton";
import StreakItem from "../../components/StreakItem/StreakItem";
import { useSetAtom } from "jotai";
import { gameDetailsDrawerSlugAtom } from "../../jotai/atoms/games";
import CardIcon from "../../components/icons/CardIcon";
import { ThemeVariant } from "../../utils/constants/generic-types";
import PencilIcon from "../../components/icons/PencilIcon";
import AvatarSection from "../../components/create-username-page/AvatarSection/AvatarSection";
import ImageAddToHome from "../../images/add-to-home.png";
import NotificationFlow from "../../components/notification-flow/NotificationFlow";
import useNotifications from "../../hooks/useNotifications";
import useSubscription from "../../react-query/queries/use-subscription-status";
import useGameplayResultsQuery from "../../react-query/queries/use-gameplay-results";

function LoadingSkeleton() {
  return (
    <div className="d-flex flex-column px-3 py-3 gap-2">
      <Skeleton height={16} width={60} theme={ThemeVariant.DARK} />
      <Skeleton height={164} width="100%" theme={ThemeVariant.DARK} />
    </div>
  );
}

function AddToHomeButton() {
  const { t } = useTranslation("common");
  const { openPwaInstallationDialog, ...notifications } = useNotifications();

  return (
    <>
      <button className="add-to-home-btn" onClick={openPwaInstallationDialog}>
        <Image
          src={ImageAddToHome}
          alt="Add to home screen"
          height={48}
          width={48}
        />
        <Stack as="p" gap={1} className="align-items-start">
          <span className="main-copy">
            {t("profile.add_to_your_home_screen")}
          </span>
          <span className="sub-copy">
            {t("profile.get_back_into_the_action_in_one_tap")}
            {t("punctuations.full_stop")}
          </span>
        </Stack>
        <ArrowRightIcon fill="#6D6876" />
      </button>

      <NotificationFlow {...notifications} />
    </>
  );
}

function ProfilePage() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { isSubscribed, isLoading: isSubscriptionLoading } = useSubscription();

  const { data: gameplayResults } = useGameplayResultsQuery({ limit: 5 });
  const { data: myTopGames, isLoading: isTopGamesLoading } =
    useMyTopGamesQuery();

  const {
    isOpen: isDownloadAppPromptOpen,
    onClose: onCloseDownloadAppPrompt,
    onOpen: onOpenDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isSignOutModalOpen,
    onClose: onCloseSignOutModal,
    onOpen: onOpenSignOutModal,
  } = useDisclosure();

  return (
    <>
      {renderHeaders({
        title: t("metadata:sigma_profile_title"),
        description: t("metadata:sigma_profile_description"),
        ogTitle: extractOgTitleFromMainTitle(t("metadata:sigma_profile_title")),
      })}

      <div className="profile-page">
        <HeaderWithTitleAndBackButton title={{ text: t("profile.profile") }} />

        <Stack as="section" gap={2} className="align-items-center hero">
          <AvatarSection
            EditIcon={PencilIcon}
            containerClass="user-avatar"
            handleEdit={() => navigate(ROUTES.ROOT.EDIT_PROFILE)}
          />
          <p className="username">@{userProfile?.username}</p>
          {!!gameplayResults?.length && (
            <div className="d-flex gap-1">
              {[...gameplayResults].reverse().map((result, i) => (
                <StreakItem key={i} result={result} />
              ))}
            </div>
          )}
        </Stack>

        <AddToHomeButton />

        {isTopGamesLoading ? (
          <LoadingSkeleton />
        ) : (
          !!myTopGames?.records?.length && (
            <section>
              <h2 className="section-heading">{t("profile.your_top_games")}</h2>
              <div className="card">
                {myTopGames?.records?.map(
                  ({ game: { name, slug, id, thumbnail }, lastPlayedAt }) => {
                    const lastPlayedInfo = getTimeDifferenceToNow(lastPlayedAt);
                    const lastPlayed = `${Math.abs(
                      lastPlayedInfo?.difference || 0
                    )} ${lastPlayedInfo?.unit}${
                      lastPlayedInfo?.difference === 1 ? "" : "s"
                    } ago`;
                    return (
                      <GameItem
                        key={id}
                        slug={slug}
                        thumbnail={thumbnail}
                        gameName={name}
                        lastPlayed={lastPlayed}
                      />
                    );
                  }
                )}
              </div>
            </section>
          )
        )}

        {!isSubscriptionLoading && isSubscribed && (
          <section>
            <h2 className="section-heading">{t("profile.settings")}</h2>
            <div className="card">
              {/* TODO: Revert this when we bring back the invite friends flow */}
              {/* <ProfileItem
              image={
                <span className="icon-container">
                  <UserPlusIcon />
                </span>
              }
              href={socialLinks?.telegramUrl}
            >
              {t("profile.invite_friends")}
            </ProfileItem> */}
              <ProfileItem
                image={
                  <span className="icon-container">
                    <CardIcon />
                  </span>
                }
                href={ROUTES.ROOT.MANAGE_SUBSCRIPTION}
              >
                {t("profile.manage_subscription")}
              </ProfileItem>
            </div>
          </section>
        )}

        <section>
          <h2 className="section-heading">{t("profile.community")}</h2>
          <div className="card">
            {/* TODO: Revert this when we have a Telegram community */}
            {/* <ProfileItem image={ImageTelegram} href={socialLinks?.telegramUrl}>
              {t("profile.join_our_telegram_community")}
            </ProfileItem> */}
            <ProfileItem image={ImageTikTok} href={LINKS.SOCIAL.TIKTOK}>
              {t("profile.follow_us_on_tiktok")}
            </ProfileItem>
          </div>
        </section>

        <FilledButton
          size={ButtonSizes.MD}
          style={{
            color: "#FC4E5D",
            background: "#FFFFFF0D",
            boxShadow: "0 2px 12px 0 rgba(25, 13, 86, 0.10)",
          }}
          onClick={onOpenSignOutModal}
        >
          {t("profile.sign_out")}
        </FilledButton>
        <footer>
          <a
            href={LINKS.WEBPAGE.SIGMA_LANDING_PAGE}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-footer-link"
          >
            {t("profile.about_sigma")}
          </a>
          <span className="dot" />
          <a
            href={LINKS.WEBPAGE.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-footer-link text-capitalize"
          >
            {t("onboarding.privacy_policy")}
          </a>
          <span className="dot" />
          <a
            href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-footer-link"
          >
            {t("onboarding.terms_and_conditions")}
          </a>
        </footer>

        <DownloadAppPrompt
          isOpen={isDownloadAppPromptOpen}
          onOpen={onOpenDownloadAppPrompt}
          onClose={onCloseDownloadAppPrompt}
        />
        <SignOutModal show={isSignOutModalOpen} onHide={onCloseSignOutModal} />
      </div>
    </>
  );
}
export default ProfilePage;

interface GameItemProps {
  thumbnail?: string;
  gameName?: string;
  lastPlayed?: string;
  slug?: string;
}

function GameItem({ thumbnail, gameName, lastPlayed, slug }: GameItemProps) {
  const { t } = useTranslation("common");
  const setDrawerSlug = useSetAtom(gameDetailsDrawerSlugAtom);
  return (
    <button onClick={() => setDrawerSlug(slug)} className="game-item w-100">
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="img-text-wrapper d-flex align-items-center">
          {thumbnail ? (
            <Image
              src={thumbnail}
              alt=""
              height={40}
              width={40}
              style={{ borderRadius: 8 }}
            />
          ) : (
            <ImageIcon height={40} width={40} fill="#6c53e8" />
          )}
          <Stack gap={1}>
            <p className="game-name">{gameName}</p>
            <p className="last-played">
              {t("profile.last_played")}
              {t("punctuations.colon")} <span>{lastPlayed}</span>
            </p>
          </Stack>
        </div>
        <ArrowRightIcon fill="#6D6876" />
      </div>
    </button>
  );
}

interface ProfileItemProps {
  image?: string | React.ReactNode;
  href?: string;
  isExternalLink?: boolean;
}

function ProfileItem({
  image,
  href = "#!",
  isExternalLink,
  children,
}: PropsWithChildren<ProfileItemProps>) {
  if (isExternalLink) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="profile-item w-100"
      >
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="img-text-wrapper d-flex align-items-center">
            {typeof image === "string" ? (
              <Image src={image} alt="" height={32} width={32} />
            ) : (
              image
            )}
            <div className="cta">{children}</div>
          </div>
          <ArrowRightIcon fill="#6D6876" />
        </div>
      </a>
    );
  }

  return (
    <Link to={href} className="profile-item w-100">
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="img-text-wrapper d-flex align-items-center">
          {typeof image === "string" ? (
            <Image src={image} alt="" height={32} width={32} />
          ) : (
            image
          )}
          <div className="cta">{children}</div>
        </div>
        <ArrowRightIcon fill="#6D6876" />
      </div>
    </Link>
  );
}
